import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Search, Loader2, Folder, FolderOpen, ChevronRight, Home, Check, Image, X, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ImagePickerModal - Browse and select images from Picture Manager
 * 
 * Props:
 * - isOpen: boolean - Controls modal visibility
 * - onClose: function - Called when modal should close
 * - onSelect: function(image) - Called when an image is selected
 * - maxSelections: number - Maximum number of images that can be selected (default: 1)
 * - currentImages: array - Currently selected images (to show already selected state)
 */
const ImagePickerModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  maxSelections = 1,
  currentImages = []
}) => {
  const [loading, setLoading] = useState(true);
  const [folderTree, setFolderTree] = useState({ folders: [], uncategorized_count: 0 });
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  const [selectedImages, setSelectedImages] = useState(new Set());

  // Categories and tags
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTags, setFilterTags] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch folder tree and categories/tags
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [treeRes, catsRes, tagsRes] = await Promise.all([
        axios.get(`${API}/picture-manager/folders/tree`),
        axios.get(`${API}/picture-manager/categories`),
        axios.get(`${API}/picture-manager/tags`)
      ]);
      setFolderTree(treeRes.data);
      setCategories(catsRes.data);
      setTags(tagsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch images
  const fetchImages = useCallback(async (folderId, page = 1, search = '', categoryId = 'all', tagList = []) => {
    try {
      setImagesLoading(true);
      const params = { page, per_page: 12 };
      if (folderId === 'uncategorized') {
        params.folder_id = 'uncategorized';
      } else if (folderId) {
        params.folder_id = folderId;
      }
      if (search) {
        params.search = search;
      }
      if (categoryId && categoryId !== 'all') {
        params.category_id = categoryId;
      }
      if (tagList.length > 0) {
        params.tags = tagList.join(',');
      }
      const response = await axios.get(`${API}/picture-manager/images`, { params });
      setImages(response.data.items);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        total_pages: response.data.total_pages
      });
    } catch (error) {
      console.error('Failed to fetch images:', error);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (isOpen) {
      fetchData();
      setSelectedImages(new Set());
      setSearchQuery('');
      setCurrentFolder(null);
      setBreadcrumb([]);
      setFilterCategory('all');
      setFilterTags([]);
    }
  }, [isOpen, fetchData]);

  // Fetch images when folder or filters change
  useEffect(() => {
    if (isOpen) {
      const folderId = currentFolder?.id || (currentFolder === 'uncategorized' ? 'uncategorized' : null);
      fetchImages(folderId, 1, searchQuery, filterCategory, filterTags);
    }
  }, [isOpen, currentFolder, fetchImages, searchQuery, filterCategory, filterTags]);

  // Navigate to folder
  const navigateToFolder = (folder) => {
    if (folder === null) {
      setCurrentFolder(null);
      setBreadcrumb([]);
    } else if (folder === 'uncategorized') {
      setCurrentFolder('uncategorized');
      setBreadcrumb([{ id: 'uncategorized', name: 'Uncategorized' }]);
    } else {
      setCurrentFolder(folder);
      const pathParts = folder.path.split('/');
      const newBreadcrumb = pathParts.map((name, index) => {
        const partialPath = pathParts.slice(0, index + 1).join('/');
        const matchingFolder = findFolderByPath(folderTree.folders, partialPath);
        return { id: matchingFolder?.id || folder.id, name };
      });
      setBreadcrumb(newBreadcrumb);
    }
  };

  // Find folder by path recursively
  const findFolderByPath = (folders, path) => {
    for (const folder of folders) {
      if (folder.path === path) return folder;
      if (folder.children?.length) {
        const found = findFolderByPath(folder.children, path);
        if (found) return found;
      }
    }
    return null;
  };

  // Toggle image selection
  const toggleImageSelection = (image) => {
    // Check if image is already in currentImages
    const isAlreadyUsed = currentImages.some(img => img.url === image.url);
    if (isAlreadyUsed) {
      toast.info('This image is already added');
      return;
    }

    const newSelection = new Set(selectedImages);
    if (newSelection.has(image.id)) {
      newSelection.delete(image.id);
    } else {
      if (maxSelections === 1) {
        // Single selection mode - replace
        newSelection.clear();
        newSelection.add(image.id);
      } else if (newSelection.size < maxSelections) {
        // Multi selection mode - add if under limit
        newSelection.add(image.id);
      } else {
        toast.warning(`Maximum ${maxSelections} images can be selected`);
        return;
      }
    }
    setSelectedImages(newSelection);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    const selected = images.filter(img => selectedImages.has(img.id));
    if (selected.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    // Call onSelect for each selected image, pre-filling caption from library
    selected.forEach(img => {
      onSelect({
        id: img.id,
        url: img.url,
        thumbnail_url: img.thumbnail_url,
        caption: img.caption || '',   // pre-fill from Picture Manager; editable per-use
        order: currentImages.length,
        from_library: true,
        original_filename: img.original_filename
      });
    });

    // Update usage count in backend (fire and forget)
    selected.forEach(img => {
      axios.put(`${API}/picture-manager/images/${img.id}/increment-usage`).catch(() => {});
    });

    onClose();
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Toggle filter tag
  const toggleFilterTag = (tagName) => {
    if (filterTags.includes(tagName)) {
      setFilterTags(filterTags.filter(t => t !== tagName));
    } else {
      setFilterTags([...filterTags, tagName]);
    }
  };

  // Render folder tree item
  const renderFolderItem = (folder, depth = 0) => {
    const hasChildren = folder.children?.length > 0;
    const isActive = currentFolder?.id === folder.id;

    return (
      <div key={folder.id}>
        <div
          onClick={() => navigateToFolder(folder)}
          className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
            isActive ? 'bg-[#14B8A6]/10 text-[#14B8A6]' : ''
          }`}
          style={{ paddingLeft: `${8 + depth * 12}px` }}
        >
          <Folder className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
          <span className="text-sm truncate flex-1">{folder.name}</span>
          <span className="text-xs text-gray-400">({folder.image_count})</span>
        </div>
        {hasChildren && folder.children.map(child => renderFolderItem(child, depth + 1))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-[#14B8A6]" />
            Choose from Image Library
          </DialogTitle>
        </DialogHeader>

        {/* Best practices reminder */}
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex-shrink-0">
          <span className="font-medium">Tip:</span> Compress images before uploading (
          <a href="https://tinyjpg.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">TinyJPG</a>,{' '}
          <a href="https://compressor.io" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">Compressor.io</a>).
          Watermark originals via{' '}
          <a href="https://watermarkly.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">Watermarkly</a>.
          Captions set in Picture Manager will pre-fill here automatically.
        </div>

        <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
          {/* Sidebar - Folders */}
          <div className="w-48 flex-shrink-0 border-r pr-4 overflow-y-auto">
            <p className="text-xs font-medium text-gray-500 mb-2">FOLDERS</p>
            
            {/* All Images */}
            <div
              onClick={() => navigateToFolder(null)}
              className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
                currentFolder === null ? 'bg-[#14B8A6]/10 text-[#14B8A6]' : ''
              }`}
            >
              <Home className="w-4 h-4" />
              <span className="text-sm">All Images</span>
            </div>

            {/* Uncategorized */}
            <div
              onClick={() => navigateToFolder('uncategorized')}
              className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
                currentFolder === 'uncategorized' ? 'bg-[#14B8A6]/10 text-[#14B8A6]' : ''
              }`}
            >
              <FolderOpen className="w-4 h-4 text-gray-400" />
              <span className="text-sm">Uncategorized</span>
              <span className="text-xs text-gray-400">({folderTree.uncategorized_count})</span>
            </div>

            {/* Folder Tree */}
            {loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="mt-2">
                {folderTree.folders.map(folder => renderFolderItem(folder))}
              </div>
            )}

            {/* Category Filter */}
            {categories.length > 0 && (
              <>
                <p className="text-xs font-medium text-gray-500 mt-4 mb-2">CATEGORY</p>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-full h-8 text-xs">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            {/* Tag Filter */}
            {tags.length > 0 && (
              <>
                <p className="text-xs font-medium text-gray-500 mt-4 mb-2">TAGS</p>
                <div className="flex flex-wrap gap-1">
                  {tags.slice(0, 8).map(tag => (
                    <Badge
                      key={tag.id}
                      variant={filterTags.includes(tag.name) ? 'default' : 'outline'}
                      className="cursor-pointer text-xs py-0"
                      style={filterTags.includes(tag.name) ? { backgroundColor: tag.color || '#14B8A6' } : {}}
                      onClick={() => toggleFilterTag(tag.name)}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
                {filterTags.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 h-6 px-2 text-xs"
                    onClick={() => setFilterTags([])}
                  >
                    Clear tags
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Search and Breadcrumb */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search images..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              {/* Breadcrumb */}
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <span 
                  className="cursor-pointer hover:text-[#14B8A6]"
                  onClick={() => navigateToFolder(null)}
                >
                  Root
                </span>
                {breadcrumb.map((crumb, index) => (
                  <React.Fragment key={crumb.id}>
                    <ChevronRight className="w-4 h-4" />
                    <span 
                      className={`cursor-pointer hover:text-[#14B8A6] ${
                        index === breadcrumb.length - 1 ? 'text-[#14B8A6] font-medium' : ''
                      }`}
                    >
                      {crumb.name}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Image Grid */}
            <div className="flex-1 overflow-y-auto">
              {imagesLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 animate-spin text-[#14B8A6]" />
                </div>
              ) : images.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                  <Image className="w-12 h-12 mb-2 text-gray-300" />
                  <p>No images found</p>
                  <p className="text-sm">Try different filters or search term</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {images.map(image => {
                    const isSelected = selectedImages.has(image.id);
                    const isAlreadyUsed = currentImages.some(img => img.url === image.url);

                    return (
                      <div
                        key={image.id}
                        onClick={() => toggleImageSelection(image)}
                        className={`relative group rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-[#14B8A6] ring-2 ring-[#14B8A6]/20' 
                            : isAlreadyUsed
                            ? 'border-gray-300 opacity-50 cursor-not-allowed'
                            : 'border-gray-200 hover:border-[#14B8A6]'
                        }`}
                      >
                        {/* Selection indicator */}
                        <div
                          className={`absolute top-2 left-2 z-10 w-6 h-6 rounded flex items-center justify-center transition-opacity ${
                            isSelected
                              ? 'bg-[#14B8A6] text-white opacity-100'
                              : isAlreadyUsed
                              ? 'bg-gray-400 text-white opacity-100'
                              : 'bg-white/80 opacity-0 group-hover:opacity-100'
                          }`}
                        >
                          {isSelected || isAlreadyUsed ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <div className="w-4 h-4 border-2 border-gray-400 rounded" />
                          )}
                        </div>

                        {/* Already used badge */}
                        {isAlreadyUsed && (
                          <div className="absolute top-2 right-2 z-10">
                            <Badge variant="secondary" className="text-xs">Added</Badge>
                          </div>
                        )}

                        {/* Category indicator */}
                        {image.category && !isAlreadyUsed && (
                          <div className="absolute top-2 right-2 z-10">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: image.category.color }}
                              title={image.category.name}
                            />
                          </div>
                        )}

                        {/* Thumbnail */}
                        <div className="aspect-square bg-gray-100">
                          <img
                            src={`${BACKEND_URL}${image.thumbnail_url || image.url}`}
                            alt={image.original_filename}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>

                        {/* Info */}
                        <div className="p-2 bg-white">
                          <p className="text-xs font-medium truncate">{image.original_filename}</p>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500">
                              {image.width}×{image.height}
                            </p>
                            {image.tags?.length > 0 && (
                              <div className="flex gap-1">
                                {image.tags.slice(0, 2).map(tag => (
                                  <span key={tag} className="text-xs text-gray-400">{tag}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => fetchImages(
                      currentFolder?.id || (currentFolder === 'uncategorized' ? 'uncategorized' : null),
                      pagination.page - 1,
                      searchQuery,
                      filterCategory,
                      filterTags
                    )}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-500">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.total_pages}
                    onClick={() => fetchImages(
                      currentFolder?.id || (currentFolder === 'uncategorized' ? 'uncategorized' : null),
                      pagination.page + 1,
                      searchQuery,
                      filterCategory,
                      filterTags
                    )}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4 mt-4">
          <div className="flex items-center justify-between w-full">
            <span className="text-sm text-gray-500">
              {selectedImages.size} image{selectedImages.size !== 1 ? 's' : ''} selected
              {maxSelections > 1 && ` (max ${maxSelections})`}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleConfirm}
                disabled={selectedImages.size === 0}
                className="bg-[#14B8A6] hover:bg-[#0D9488]"
              >
                <Check className="w-4 h-4 mr-2" />
                Add Selected
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImagePickerModal;
